import React from 'react'
import MediaQuery from 'react-responsive'

import { AcornsIcon } from '@acorns/icons'
import { Spinner } from '@acorns/web-components'
import styled from 'styled-components'

import withdrawLogo from 'assets/images/withdraw-zero-balance.png'
import ConfirmationModal from 'components/ConfirmationModal'
import useWindowDimensions from 'hooks/useWindowDimensions'
import themeValues, { style } from 'theme'
import { USDFormatter } from 'utils/number/currency'

import {
  RedStyledCloseAccountButton,
  StyledBody,
  StyledCard,
  StyledImage,
} from '../components'
import { MergedProps } from './index'
import { CloseLaterConfirmationModalViewed } from './segment'

const StyledRedButton = styled(RedStyledCloseAccountButton)`
  width: auto;
`

const Card = styled(StyledCard)<{ innerHeight?: number }>`
  @media screen and (max-width: ${style(
      (theme) => theme.breakpoints.sm - 1,
    )}px) {
    display: flex;
    justify-content: space-between;
    height: ${(props) => `calc(${props.innerHeight}px - 78px)`};
  }
`

const WVCard = styled(StyledCard)<{ innerHeight?: number }>`
  display: flex;
  justify-content: space-between;
  height: ${(props) => `calc(${props.innerHeight}px - 80px)`};
  &&& {
    border-radius: 0;
  }
`
const BodyContainer = styled.div`
  @media screen and (max-width: ${style(
      (theme) => theme.breakpoints.sm - 1,
    )}px) {
    height: auto;
    padding-top: 50px;
  }
  @media screen and (max-width: 375px) {
    padding-top: 10px;
  }
`

const ProcessingWithdrawls = styled.div`
  height: 60px;
  max-width: 500px;
  min-width: 400px;
  margin: 0 auto;
  border: 1px solid ${style((theme) => theme.colors.ivory)};
  border-radius: ${style((theme) => theme.styles.borderRadius)};
  @media screen and (max-width: ${style(
      (theme) => theme.breakpoints.sm - 1,
    )}px) {
    min-width: 200px;
  }
`

const Item = styled.div`
  height: 100%;
  min-width: 100%;
  padding: 10px 20px;
  align-items: center;
  justify-content: space-between;
  display: flex;
`

const StyledIcon = styled.div`
  float: left;
  margin-top: 4px;
`
const Product = styled.span`
  display: inline-block;
  vertical-align: middle;
  margin-top: 5px;
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0px;
  line-height: 22px;
  color: ${style((theme) => theme.colors.slate)};
  margin-left: 12px;
`

const Amount = styled.span`
  margin-top: 5px;
  font-size: 14px;
  font-weight: normal;
  letter-spacing: 0px;
  line-height: 22px;
  text-align: right;
  color: ${style((theme) => theme.colors.slate)};
  float: right;
`
const WrappingDiv = styled.div`
  display: inline-block;
  align-items: flex-start;
`
const ButtonContainer = styled.div`
  border-top: 1px solid ${style((theme) => theme.colors.ivory)};
  @media screen and (max-width: ${style(
      (theme) => theme.breakpoints.sm - 1,
    )}px) {
    border-top: none;
    position: relative;
    width: 100%;
  }
`

const Footnote = styled.div`
  font-size: 12px;
  font-weight: normal;
  letter-spacing: 0px;
  line-height: 16px;
  text-align: center;
  margin-bottom: 50px;
  margin-top: 30px;
  color: ${style((theme) => theme.colors.stone)};
  @media screen and (max-width: ${style(
      (theme) => theme.breakpoints.sm - 1,
    )}px) {
    margin-top: 0;
    margin-bottom: 20px;
  }
`

export const WithdrawWithBalanceConfirmation: React.SFC<MergedProps> = ({
  handleCloseAccountSelection,
  modalActions,
  modalBody,
  modalHeader,
  setShowCloseAccountModal,
  showCloseAccountModal,
  userData,
  fullWithdrawItem,
  recurringInvestments,
  isWebView,
}) => {
  const CardComponent = isWebView ? WVCard : Card
  const { height } = useWindowDimensions()
  return (
    <>
      <CardComponent innerHeight={height}>
        <BodyContainer>
          <StyledImage src={withdrawLogo} />

          <StyledBody>
            Your money will be transferred to your{' '}
            <b>
              {userData?.fundingSource?.name} {userData?.fundingSource?.last4}
            </b>{' '}
            account, then we’ll automatically close your Later account.
          </StyledBody>
          <ProcessingWithdrawls>
            <Item>
              <WrappingDiv>
                <StyledIcon>
                  <AcornsIcon icon={AcornsIcon.Icon.Later} width="24px" />
                </StyledIcon>
                <Product>Later</Product>
              </WrappingDiv>
              {fullWithdrawItem !== undefined ? (
                <Amount data-testid="later_balance">
                  {USDFormatter.format(fullWithdrawItem * -1)}
                </Amount>
              ) : (
                <Spinner height={20} width={20} />
              )}
            </Item>
          </ProcessingWithdrawls>
          <MediaQuery minWidth={themeValues.breakpoints.sm}>
            <Footnote>
              Transfers may take up to 3-6 business days to settle after your
              shares have been sold.
            </Footnote>
          </MediaQuery>
        </BodyContainer>
        <ButtonContainer>
          <MediaQuery maxWidth={themeValues.breakpoints.sm - 1}>
            <Footnote>
              Transfers may take up to 3-6 business days to settle after your
              shares have been sold.
            </Footnote>
          </MediaQuery>
          <StyledRedButton
            onClick={handleCloseAccountSelection}
            disabled={recurringInvestments === undefined}
          >
            Withdraw and request closure
          </StyledRedButton>
        </ButtonContainer>
      </CardComponent>

      {showCloseAccountModal && (
        <ConfirmationModal
          actions={modalActions}
          body={modalBody}
          header={modalHeader}
          onRequestClose={() =>
            setShowCloseAccountModal(!showCloseAccountModal)
          }
          isOpen={showCloseAccountModal}
          eventFields={CloseLaterConfirmationModalViewed}
        />
      )}
    </>
  )
}
